import React, { useEffect, useMemo, useState } from "react";
import { Attention } from "@optimizely/axiom";
import { useProducts } from "../../../../hooks/useProducts/useProducts";
import { useUserContext } from "../../../../providers/UserProvider";
import { TableSkeleton } from "../../Skeleton/TableSkeleton";
import { useAccessList } from "../../../../hooks/useAccessList/useAccessList";
import { AccessDetailsTable } from "../AccessDetailsTable/AccessDetailsTable";
import { getAccessByProduct, updateAccessWithProjectDetails } from "../../../../lib/access-helpers";
import {
    CustomGroupAccess,
    CustomGroupAccessStatus,
    useAccessFlowUserFormContext
} from "../AccessManagementUserForm/AccessManagementUserFormProvider";
import { AccessDetailsByProductId } from "../../../../domain/AccessDetails";
import { UserGroup } from "../../../../domain/UserGroup";

type CustomGroupAccessDetailsProps = {
    userGroup: UserGroup;
};

/**
 * The result of one product-instance lookup, tagged with the group it was fetched for.
 *
 * The group id travels with the result rather than sitting in its own state because the two are
 * read together and must never disagree: the modal swaps `userGroup` on a mounted instance, so for
 * one commit after a group change this state still describes the previous group. Publishing it
 * would hand the new group's modal the old group's access under a "loaded" status, which re-enables
 * confirmation against the wrong group (EPTS-6429).
 */
type GroupAccessLookup = {
    groupId: string;
    productList: AccessDetailsByProductId;
    status: CustomGroupAccessStatus;
};

/** Stable identity so the derived-state effect below does not re-run on every render. */
const NO_LOOKUP: GroupAccessLookup = { groupId: "", productList: {}, status: "loading" };

export const CustomGroupAccessDetails = ({ userGroup }: CustomGroupAccessDetailsProps) => {
    const { organizationId = "" } = useUserContext();
    const { getUserGroupInstances } = useProducts({ organizationId });
    const { accessList: availableInstanceAccess } = useAccessList({
        organizationId,
        instanceIds: userGroup.instancePermissions.map((ip) => ip.instanceId)
    });
    // An empty productList means "no product access" only once the fetch below has succeeded. The
    // consumers of the shared context branch on that distinction, so it has to be published rather
    // than inferred from the (identical) empty accessDetails map (EPTS-6429).
    const [lookup, setLookup] = useState<GroupAccessLookup>(NO_LOOKUP);
    const [accessDetails, setAccessDetails] = useState<AccessDetailsByProductId>({});
    const [groupDetailsLoading, setGroupDetailsLoading] = useState(true);
    const { setUpdateCustomGroupAccess } = useAccessFlowUserFormContext();

    // Anything fetched for a different group is not an answer about this one. Until the current
    // group's own lookup lands, the absence of a result is reported as "loading" rather than the
    // previous group's rows.
    const currentLookup = useMemo(() => (lookup.groupId === userGroup.id ? lookup : NO_LOOKUP), [lookup, userGroup.id]);

    useEffect(() => {
        // The modal keeps this instance mounted across group changes, so a slow lookup for a
        // previously selected group can resolve after a newer one. Last-write-wins would publish
        // the stale group's access as "loaded" and enable confirmation against it, so responses
        // that are no longer current are dropped (EPTS-6429).
        let isCurrent = true;

        if (!!userGroup.id) {
            // Re-show the skeleton and drop any previous result: until the new lookup lands there
            // is nothing to say about this group. Clearing here also covers a refetch of the same
            // group, which the id comparison above cannot detect.
            setGroupDetailsLoading(true);
            setLookup({ groupId: userGroup.id, productList: {}, status: "loading" });
            getUserGroupInstances({ userGroupId: userGroup.id })
                .then((response) => {
                    if (!isCurrent) {
                        return;
                    }

                    const productInstances = response.items;
                    const accessByProduct = getAccessByProduct({
                        productInstances,
                        userGroup,
                        isCustomGroup: true
                    });
                    setLookup({
                        groupId: userGroup.id,
                        productList: accessByProduct || {},
                        status: "loaded"
                    });
                })

                .catch((error) => {
                    if (!isCurrent) {
                        return;
                    }

                    console.error("Failed to fetch product instances: ", error);
                    setLookup({ groupId: userGroup.id, productList: {}, status: "error" });
                })
                .finally(() => {
                    if (isCurrent) {
                        setGroupDetailsLoading(false);
                    }
                });
        }

        return () => {
            isCurrent = false;
        };
    }, [userGroup, getUserGroupInstances]);

    useEffect(() => {
        const { productList, status: accessDetailsStatus } = currentLookup;
        const isProductListEmpty = Object.keys(productList).length === 0;
        let accessDetails = { ...productList };

        const hasExperimentationProductInGroupAccess =
            !isProductListEmpty && productList[process.env.REACT_APP_EXPERIMENTATION_PRODUCT_ID!];

        const hasAvailableOrgAccess = (availableInstanceAccess || []).length > 0;

        const needsProjectDetails = hasExperimentationProductInGroupAccess && hasAvailableOrgAccess && !!userGroup.id;

        if (needsProjectDetails) {
            setGroupDetailsLoading(true);
            accessDetails = updateAccessWithProjectDetails({
                productList,
                availableOrgAccess: availableInstanceAccess,
                groupId: userGroup.id
            });
            setGroupDetailsLoading(false);
        }

        setAccessDetails(accessDetails);
        setUpdateCustomGroupAccess((prev): CustomGroupAccess => {
            return { ...prev, accessDetails, accessDetailsStatus };
        });
    }, [currentLookup, userGroup.id, setUpdateCustomGroupAccess, availableInstanceAccess]);

    if (groupDetailsLoading) {
        return <TableSkeleton className="push-double--top push-double--bottom push--left" />;
    }

    // Rendering an empty access table here would read as "this group grants nothing", which is the
    // one thing we cannot claim after a failed lookup. Deliberately states the fact without
    // prescribing an action: whether the user can still confirm depends on whether the flow needs
    // these details (see CustomGroupAccessConfirmationModal's isConfirmDisabled).
    if (currentLookup.status === "error") {
        return (
            <Attention alignment="left" type="bad-news">
                We couldn't load this group's product access details.
            </Attention>
        );
    }

    return <AccessDetailsTable accessDetails={accessDetails} />;
};

CustomGroupAccessDetails.displayName = "CustomGroupAccessDetails";
